import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "../hooks/useMediaQuery";
import {
	Check,
	Smartphone,
	Globe,
	Calendar,
	ChevronRight,
	ExternalLink,
	Apple,
	Play,
	QrCode,
	Gift,
} from "lucide-react";

// Assets
import Restaurant from "../assets/restaurant/restaurant-app.png";
import RestaurantWeb from "../assets/restaurant/cantina-mac.png";
import Reservas from "../assets/restaurant/iframe-res.png";
import CartaDigital from "../assets/restaurant/carta.png"; // TODO: Canviar per imatge de carta digital
import Fidelizacion from "../assets/restaurant/loyalty.png"; // TODO: Canviar per imatge de fidelització

// Mockups de ejemplo
import mockupCantina from "../assets/restaurant/la-cantina.png";
import mockupTavola from "../assets/restaurant/tavola.png";
import mockupBrunch from "../assets/restaurant/brunch-blossom.png";

const primaryColor = "#43bd98";

const RestaurantDemo = () => {
	const { t } = useTranslation();
	const isMobile = useMediaQuery("(max-width: 768px)");
	const [start, setStart] = useState(false);

	useEffect(() => {
		const timer = setTimeout(() => setStart(true), 100);
		return () => clearTimeout(timer);
	}, []);

	// Ejemplos reales de restaurantes
	const examples = [
		{
			name: "La Cantina",
			mockup: mockupCantina,
			playStore: "https://play.google.com/store/apps/details?id=com.applaby.lacantina",
			appStore: "https://apps.apple.com/us/app/la-cantina/id6742712477",
			web: "https://www.lacantinarestaurante.com/",
			orders: "https://orders.applaby.com/la-cantina",
		},
		{
			name: "La Tavola",
			mockup: mockupTavola,
			playStore: "https://play.google.com/store/apps/details?id=com.applaby.latavola",
			appStore: "https://apps.apple.com/us/app/la-tavola/id6744865943",
			web: "https://www.latavolaitaliana.es/",
			orders: "https://orders.applaby.com/la-tavola",
		},
		{
			name: "Brunch & Blossom",
			mockup: mockupBrunch,
			playStore: "https://play.google.com/store/apps/details?id=com.applaby.brunchblossom",
			appStore: "https://apps.apple.com/us/app/brunch-blossom/id6745568150",
			web: "https://www.brunchblossom.com/",
			orders: "https://orders.applaby.com/brunch-blossom",
		},
	];

	return (
		<div style={{ width: "100%", overflowX: "hidden", fontFamily: "'Poppins', sans-serif" }}>
			
			{/* HERO - Explora ejemplos reales */}
			<section
				style={{
					background: "linear-gradient(135deg, #f0fdf9 0%, #ecfdf5 50%, #f0fdf4 100%)",
					padding: isMobile ? "100px 20px 60px" : "140px 40px 80px",
					textAlign: "center",
				}}
			>
				<div style={{ maxWidth: "900px", margin: "0 auto" }}>
					<motion.div
						initial={{ opacity: 0, scale: 0.9 }}
						animate={start ? { opacity: 1, scale: 1 } : {}}
						transition={{ duration: 0.5 }}
						style={{
							display: "inline-block",
							background: primaryColor,
							color: "#fff",
							padding: "8px 18px",
							borderRadius: "24px",
							fontSize: "0.85rem",
							fontWeight: 600,
							marginBottom: "20px",
						}}
					>
						{t("restaurantDemo.hero.badge")}
					</motion.div>

					<motion.h1
						initial={{ opacity: 0, y: 30 }}
						animate={start ? { opacity: 1, y: 0 } : {}}
						transition={{ duration: 0.6, delay: 0.1 }}
						style={{
							fontSize: isMobile ? "2rem" : "3rem",
							fontWeight: 800,
							color: "#111827",
							lineHeight: 1.2,
							marginBottom: "1rem",
						}}
					>
						{t("restaurantDemo.hero.title")}
					</motion.h1>

					<motion.p
						initial={{ opacity: 0, y: 20 }}
						animate={start ? { opacity: 1, y: 0 } : {}}
						transition={{ duration: 0.6, delay: 0.2 }}
						style={{
							fontSize: isMobile ? "1rem" : "1.15rem",
							color: "#4b5563",
							lineHeight: 1.7,
							maxWidth: "600px",
							margin: "0 auto 2rem",
						}}
					>
						{t("restaurantDemo.hero.subtitle")}
					</motion.p>

					<motion.div
						initial={{ opacity: 0, y: 20 }}
						animate={start ? { opacity: 1, y: 0 } : {}}
						transition={{ duration: 0.6, delay: 0.3 }}
						style={{
							display: "flex",
							gap: "12px",
							justifyContent: "center",
							flexWrap: "wrap",
						}}
					>
						<a
							href="#ejemplos"
							style={{
								display: "inline-flex",
								alignItems: "center",
								gap: "8px",
								background: `linear-gradient(135deg, ${primaryColor} 0%, #2fae83 100%)`,
								color: "#fff",
								padding: "14px 28px",
								borderRadius: "12px",
								fontWeight: 600,
								fontSize: "1rem",
								textDecoration: "none",
								boxShadow: "0 8px 24px rgba(67,189,152,0.3)",
							}}
						>
							{t("restaurantDemo.hero.ctaPrimary")}
							<ChevronRight size={18} />
						</a>
						<a
							href="https://creator.applaby.com?onboarding=true&appType=Ecommerce&appSubtype=Restaurant"
							target="_blank"
							rel="noreferrer"
							style={{
								display: "inline-flex",
								alignItems: "center",
								gap: "8px",
								background: "#fff",
								color: "#374151",
								padding: "14px 28px",
								borderRadius: "12px",
								fontWeight: 600,
								fontSize: "1rem",
								textDecoration: "none",
								border: "1px solid #d1d5db",
							}}
						>
							{t("restaurantDemo.hero.ctaSecondary")}
						</a>
					</motion.div>
				</div>
			</section>

			{/* EJEMPLOS REALES */}
			<section
				id="ejemplos"
				style={{
					padding: isMobile ? "60px 20px" : "80px 40px",
					background: "#fff",
				}}
			>
				<div style={{ maxWidth: "1100px", margin: "0 auto" }}>
					<motion.div
						initial={{ opacity: 0, y: 30 }}
						whileInView={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.6 }}
						viewport={{ once: true }}
						style={{ textAlign: "center", marginBottom: "3rem" }}
					>
						<span
							style={{
								display: "inline-block",
								background: "#e6f9f3",
								color: primaryColor,
								padding: "6px 14px",
								borderRadius: "20px",
								fontSize: "0.85rem",
								fontWeight: 600,
								marginBottom: "12px",
							}}
						>
							{t("restaurantDemo.examples.badge")}
						</span>
						<h2
							style={{
								fontSize: isMobile ? "1.8rem" : "2.4rem",
								fontWeight: 800,
								color: "#111827",
								marginBottom: "0.5rem",
							}}
						>
							{t("restaurantDemo.examples.title")}
						</h2>
						<p style={{ color: "#6b7280", fontSize: "1rem" }}>
							{t("restaurantDemo.examples.subtitle")}
						</p>
					</motion.div>

					<div
						style={{
							display: "grid",
							gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
							gap: "24px",
						}}
					>
						{examples.map((example, index) => (
							<motion.div
								key={example.name}
								initial={{ opacity: 0, y: 30 }}
								whileInView={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.5, delay: index * 0.1 }}
								viewport={{ once: true }}
								style={{
									background: "#f9fafb",
									borderRadius: "20px",
									padding: "24px",
									textAlign: "center",
								}}
							>
								<img
									src={example.mockup}
									alt={example.name}
									style={{
										width: "160px",
										height: "auto",
										margin: "0 auto 20px",
										filter: "drop-shadow(0 10px 20px rgba(0,0,0,0.1))",
									}}
								/>
								<h3
									style={{
										fontSize: "1.3rem",
										fontWeight: 700,
										color: "#111827",
										marginBottom: "16px",
									}}
								>
									{example.name}
								</h3>

								{/* Botones de tiendas */}
								<div
									style={{
										display: "flex",
										gap: "8px",
										justifyContent: "center",
										marginBottom: "12px",
									}}
								>
									<a
										href={example.appStore}
										target="_blank"
										rel="noreferrer"
										style={{
											display: "flex",
											alignItems: "center",
											gap: "6px",
											background: "transparent",
											color: "#374151",
											border: "1.5px solid #d1d5db",
											padding: "8px 14px",
											borderRadius: "8px",
											fontSize: "0.8rem",
											fontWeight: 500,
											textDecoration: "none",
										}}
									>
										<Apple size={14} /> App Store
									</a>
									<a
										href={example.playStore}
										target="_blank"
										rel="noreferrer"
										style={{
											display: "flex",
											alignItems: "center",
											gap: "6px",
											background: "transparent",
											color: "#374151",
											border: "1.5px solid #d1d5db",
											padding: "8px 14px",
											borderRadius: "8px",
											fontSize: "0.8rem",
											fontWeight: 500,
											textDecoration: "none",
										}}
									>
										<Play size={14} /> Google Play
									</a>
								</div>

								{/* Link web */}
								<a
									href={example.web}
									target="_blank"
									rel="noreferrer"
									style={{
										display: "inline-flex",
										alignItems: "center",
										justifyContent: "center",
										gap: "6px",
										background: primaryColor,
										color: "#fff",
										padding: "10px 20px",
										borderRadius: "8px",
										fontSize: "0.85rem",
										fontWeight: 600,
										textDecoration: "none",
									}}
								>
									<Globe size={14} /> Ver web
								</a>
							</motion.div>
						))}
					</div>
				</div>
			</section>

			{/* QUÉ INCLUYE - App */}
			<section
				style={{
					padding: isMobile ? "60px 20px" : "80px 40px",
					background: "#f8fefc",
				}}
			>
				<div
					style={{
						maxWidth: "1100px",
						margin: "0 auto",
						display: "grid",
						gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
						gap: "48px",
						alignItems: "center",
					}}
				>
					<motion.div
						initial={{ opacity: 0, x: -40 }}
						whileInView={{ opacity: 1, x: 0 }}
						transition={{ duration: 0.6 }}
						viewport={{ once: true }}
					>
						<div
							style={{
								display: "flex",
								alignItems: "center",
								gap: "10px",
								marginBottom: "16px",
							}}
						>
							<div
								style={{
									background: primaryColor,
									borderRadius: "12px",
									padding: "10px",
									display: "flex",
								}}
							>
								<Smartphone size={24} color="#fff" />
							</div>
							<span
								style={{
									fontSize: "0.85rem",
									fontWeight: 600,
									color: primaryColor,
									textTransform: "uppercase",
									letterSpacing: "0.5px",
								}}
							>
								{t("restaurantDemo.app.badge")}
							</span>
						</div>

						<h2
							style={{
								fontSize: isMobile ? "1.8rem" : "2.2rem",
								fontWeight: 800,
								color: "#111827",
								marginBottom: "1rem",
								lineHeight: 1.2,
							}}
						>
							{t("restaurantDemo.app.title")}
						</h2>

						<p
							style={{
								fontSize: "1rem",
								color: "#4b5563",
								lineHeight: 1.7,
								marginBottom: "1.5rem",
							}}
						>
							{t("restaurantDemo.app.description")}
						</p>

						<ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
							{t("restaurantDemo.app.features", { returnObjects: true }).map(
								(item, i) => (
									<li
										key={i}
										style={{
											display: "flex",
											alignItems: "center",
											gap: "12px",
											marginBottom: "12px",
											fontSize: "0.95rem",
											color: "#374151",
										}}
									>
										<Check
											size={20}
											color={primaryColor}
											strokeWidth={3}
										/>
										{item}
									</li>
								)
							)}
						</ul>
					</motion.div>

					<motion.div
						initial={{ opacity: 0, x: 40 }}
						whileInView={{ opacity: 1, x: 0 }}
						transition={{ duration: 0.6, delay: 0.2 }}
						viewport={{ once: true }}
						style={{ textAlign: "center" }}
					>
						<img
							src={Restaurant}
							alt="App móvil restaurante"
							style={{
								width: "100%",
								maxWidth: "350px",
								borderRadius: "20px",
							}}
						/>
					</motion.div>
				</div>
			</section>

			{/* QUÉ INCLUYE - Web */}
			<section
				style={{
					padding: isMobile ? "60px 20px" : "80px 40px",
					background: "#fff",
				}}
			>
				<div
					style={{
						maxWidth: "1100px",
						margin: "0 auto",
						display: "grid",
						gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
						gap: "48px",
						alignItems: "center",
					}}
				>
					<motion.div
						initial={{ opacity: 0, x: -40 }}
						whileInView={{ opacity: 1, x: 0 }}
						transition={{ duration: 0.6 }}
						viewport={{ once: true }}
						style={{ textAlign: "center", order: isMobile ? 2 : 1 }}
					>
						<img
							src={RestaurantWeb}
							alt="Web restaurante"
							style={{
								width: "100%",
								maxWidth: "500px",
								borderRadius: "16px",
							}}
						/>
					</motion.div>

					<motion.div
						initial={{ opacity: 0, x: 40 }}
						whileInView={{ opacity: 1, x: 0 }}
						transition={{ duration: 0.6, delay: 0.2 }}
						viewport={{ once: true }}
						style={{ order: isMobile ? 1 : 2 }}
					>
						<div
							style={{
								display: "flex",
								alignItems: "center",
								gap: "10px",
								marginBottom: "16px",
							}}
						>
							<div
								style={{
									background: primaryColor,
									borderRadius: "12px",
									padding: "10px",
									display: "flex",
								}}
							>
								<Globe size={24} color="#fff" />
							</div>
							<span
								style={{
									fontSize: "0.85rem",
									fontWeight: 600,
									color: primaryColor,
									textTransform: "uppercase",
									letterSpacing: "0.5px",
								}}
							>
								{t("restaurantDemo.web.badge")}
							</span>
						</div>

						<h2
							style={{
								fontSize: isMobile ? "1.8rem" : "2.2rem",
								fontWeight: 800,
								color: "#111827",
								marginBottom: "1rem",
								lineHeight: 1.2,
							}}
						>
							{t("restaurantDemo.web.title")}
						</h2>

						<p
							style={{
								fontSize: "1rem",
								color: "#4b5563",
								lineHeight: 1.7,
								marginBottom: "1.5rem",
							}}
						>
							{t("restaurantDemo.web.description")}
						</p>

						<a
							href="https://www.lacantinarestaurante.com/"
							target="_blank"
							rel="noreferrer"
							style={{
								display: "inline-flex",
								alignItems: "center",
								gap: "8px",
								background: primaryColor,
								color: "#fff",
								padding: "12px 24px",
								borderRadius: "10px",
								fontWeight: 600,
								fontSize: "0.95rem",
								textDecoration: "none",
							}}
						>
							{t("restaurantDemo.web.button")}
							<ExternalLink size={16} />
						</a>
					</motion.div>
				</div>
			</section>

			{/* QUÉ INCLUYE - Reservas */}
			<section
				style={{
					padding: isMobile ? "60px 20px" : "80px 40px",
					background: "#f8fefc",
				}}
			>
				<div
					style={{
						maxWidth: "1100px",
						margin: "0 auto",
						display: "grid",
						gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
						gap: "48px",
						alignItems: "center",
					}}
				>
					<motion.div
						initial={{ opacity: 0, x: -40 }}
						whileInView={{ opacity: 1, x: 0 }}
						transition={{ duration: 0.6 }}
						viewport={{ once: true }}
					>
						<div
							style={{
								display: "flex",
								alignItems: "center",
								gap: "10px",
								marginBottom: "16px",
							}}
						>
							<div
								style={{
									background: primaryColor,
									borderRadius: "12px",
									padding: "10px",
									display: "flex",
								}}
							>
								<Calendar size={24} color="#fff" />
							</div>
							<span
								style={{
									fontSize: "0.85rem",
									fontWeight: 600,
									color: primaryColor,
									textTransform: "uppercase",
									letterSpacing: "0.5px",
								}}
							>
								{t("restaurantDemo.booking.badge")}
							</span>
						</div>

						<h2
							style={{
								fontSize: isMobile ? "1.8rem" : "2.2rem",
								fontWeight: 800,
								color: "#111827",
								marginBottom: "1rem",
								lineHeight: 1.2,
							}}
						>
							{t("restaurantDemo.booking.title")}
						</h2>

						<p
							style={{
								fontSize: "1rem",
								color: "#4b5563",
								lineHeight: 1.7,
								marginBottom: "1.5rem",
							}}
						>
							{t("restaurantDemo.booking.description")}
						</p>

						<a
							href="https://www.lacantinarestaurante.com/"
							target="_blank"
							rel="noreferrer"
							style={{
								display: "inline-flex",
								alignItems: "center",
								gap: "8px",
								background: "#fff",
								color: primaryColor,
								padding: "12px 24px",
								borderRadius: "10px",
								fontWeight: 600,
								fontSize: "0.95rem",
								textDecoration: "none",
								border: `2px solid ${primaryColor}`,
							}}
						>
							{t("restaurantDemo.booking.button")}
							<ExternalLink size={16} />
						</a>
					</motion.div>

					<motion.div
						initial={{ opacity: 0, x: 40 }}
						whileInView={{ opacity: 1, x: 0 }}
						transition={{ duration: 0.6, delay: 0.2 }}
						viewport={{ once: true }}
						style={{ textAlign: "center" }}
					>
						<img
							src={Reservas}
							alt="Sistema de reservas"
							style={{
								width: "100%",
								maxWidth: "450px",
								borderRadius: "16px",
							}}
						/>
					</motion.div>
				</div>
			</section>

			{/* CARTA DIGITAL QR */}
			<section
				style={{
					padding: isMobile ? "60px 20px" : "80px 40px",
					background: "#fff",
				}}
			>
				<div
					style={{
						maxWidth: "1100px",
						margin: "0 auto",
						display: "grid",
						gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
						gap: "48px",
						alignItems: "center",
					}}
				>
					<motion.div
						initial={{ opacity: 0, x: -40 }}
						whileInView={{ opacity: 1, x: 0 }}
						transition={{ duration: 0.6 }}
						viewport={{ once: true }}
						style={{ textAlign: "center", order: isMobile ? 2 : 1 }}
					>
						{/* Browser mockup */}
						<div
							style={{
								background: "#1f2937",
								borderRadius: "12px",
								overflow: "hidden",
								boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
								maxWidth: "500px",
								margin: "0 auto",
							}}
						>
							{/* Browser header */}
							<div
								style={{
									background: "#374151",
									padding: "12px 16px",
									display: "flex",
									alignItems: "center",
									gap: "8px",
								}}
							>
								<div style={{ width: "12px", height: "12px", borderRadius: "50%", background: "#ef4444" }} />
								<div style={{ width: "12px", height: "12px", borderRadius: "50%", background: "#eab308" }} />
								<div style={{ width: "12px", height: "12px", borderRadius: "50%", background: "#22c55e" }} />
								<div
									style={{
										flex: 1,
										background: "#1f2937",
										borderRadius: "6px",
										padding: "6px 12px",
										marginLeft: "12px",
										fontSize: "0.75rem",
										color: "#9ca3af",
									}}
								>
									turestaurante.com/carta
								</div>
							</div>
							{/* Browser content */}
							<img
								src={CartaDigital}
								alt="Carta digital QR"
								style={{
									width: "100%",
									display: "block",
								}}
							/>
						</div>
					</motion.div>

					<motion.div
						initial={{ opacity: 0, x: 40 }}
						whileInView={{ opacity: 1, x: 0 }}
						transition={{ duration: 0.6, delay: 0.2 }}
						viewport={{ once: true }}
						style={{ order: isMobile ? 1 : 2 }}
					>
						<div
							style={{
								display: "flex",
								alignItems: "center",
								gap: "10px",
								marginBottom: "16px",
							}}
						>
							<div
								style={{
									background: primaryColor,
									borderRadius: "12px",
									padding: "10px",
									display: "flex",
								}}
							>
								<QrCode size={24} color="#fff" />
							</div>
							<span
								style={{
									fontSize: "0.85rem",
									fontWeight: 600,
									color: primaryColor,
									textTransform: "uppercase",
									letterSpacing: "0.5px",
								}}
							>
								{t("restaurantDemo.menu.badge")}
							</span>
						</div>

						<h2
							style={{
								fontSize: isMobile ? "1.8rem" : "2.2rem",
								fontWeight: 800,
								color: "#111827",
								marginBottom: "1rem",
								lineHeight: 1.2,
							}}
						>
							{t("restaurantDemo.menu.title")}
						</h2>

						<p
							style={{
								fontSize: "1rem",
								color: "#4b5563",
								lineHeight: 1.7,
								marginBottom: "1.5rem",
							}}
						>
							{t("restaurantDemo.menu.description")}
						</p>

						<ul style={{ listStyle: "none", padding: 0, margin: "0 0 1.5rem 0" }}>
							{t("restaurantDemo.menu.features", { returnObjects: true }).map(
								(item, i) => (
									<li
										key={i}
										style={{
											display: "flex",
											alignItems: "center",
											gap: "12px",
											marginBottom: "12px",
											fontSize: "0.95rem",
											color: "#374151",
										}}
									>
										<Check
											size={20}
											color={primaryColor}
											strokeWidth={3}
										/>
										{item}
									</li>
								)
							)}
						</ul>

						<a
							href="https://www.lacantinarestaurante.com/"
							target="_blank"
							rel="noreferrer"
							style={{
								display: "inline-flex",
								alignItems: "center",
								gap: "8px",
								background: primaryColor,
								color: "#fff",
								padding: "12px 24px",
								borderRadius: "10px",
								fontWeight: 600,
								fontSize: "0.95rem",
								textDecoration: "none",
							}}
						>
							{t("restaurantDemo.menu.button")}
							<ExternalLink size={16} />
						</a>
					</motion.div>
				</div>
			</section>

			{/* FIDELIZACIÓN */}
			<section
				style={{
					padding: isMobile ? "60px 20px" : "80px 40px",
					background: "#f8fefc",
				}}
			>
				<div
					style={{
						maxWidth: "1100px",
						margin: "0 auto",
						display: "grid",
						gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
						gap: "48px",
						alignItems: "center",
					}}
				>
					<motion.div
						initial={{ opacity: 0, x: -40 }}
						whileInView={{ opacity: 1, x: 0 }}
						transition={{ duration: 0.6 }}
						viewport={{ once: true }}
					>
						<div
							style={{
								display: "flex",
								alignItems: "center",
								gap: "10px",
								marginBottom: "16px",
							}}
						>
							<div
								style={{
									background: primaryColor,
									borderRadius: "12px",
									padding: "10px",
									display: "flex",
								}}
							>
								<Gift size={24} color="#fff" />
							</div>
							<span
								style={{
									fontSize: "0.85rem",
									fontWeight: 600,
									color: primaryColor,
									textTransform: "uppercase",
									letterSpacing: "0.5px",
								}}
							>
								{t("restaurantDemo.loyalty.badge")}
							</span>
						</div>

						<h2
							style={{
								fontSize: isMobile ? "1.8rem" : "2.2rem",
								fontWeight: 800,
								color: "#111827",
								marginBottom: "1rem",
								lineHeight: 1.2,
							}}
						>
							{t("restaurantDemo.loyalty.title")}
						</h2>

						<p
							style={{
								fontSize: "1rem",
								color: "#4b5563",
								lineHeight: 1.7,
								marginBottom: "1.5rem",
							}}
						>
							{t("restaurantDemo.loyalty.description")}
						</p>

						<ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
							{t("restaurantDemo.loyalty.features", { returnObjects: true }).map(
								(item, i) => (
									<li
										key={i}
										style={{
											display: "flex",
											alignItems: "center",
											gap: "12px",
											marginBottom: "12px",
											fontSize: "0.95rem",
											color: "#374151",
										}}
									>
										<Check
											size={20}
											color={primaryColor}
											strokeWidth={3}
										/>
										{item}
									</li>
								)
							)}
						</ul>
					</motion.div>

					<motion.div
						initial={{ opacity: 0, x: 40 }}
						whileInView={{ opacity: 1, x: 0 }}
						transition={{ duration: 0.6, delay: 0.2 }}
						viewport={{ once: true }}
						style={{ textAlign: "center" }}
					>
						<img
							src={Fidelizacion}
							alt="Sistema de fidelización"
							style={{
								width: "100%",
								maxWidth: "350px",
								borderRadius: "20px",
							}}
						/>
					</motion.div>
				</div>
			</section>

			{/* CTA FINAL */}
			<section
				style={{
					padding: isMobile ? "60px 20px" : "80px 40px",
					background: `linear-gradient(135deg, ${primaryColor} 0%, #2fae83 100%)`,
					textAlign: "center",
				}}
			>
				<div style={{ maxWidth: "700px", margin: "0 auto" }}>
					<motion.h2
						initial={{ opacity: 0, y: 30 }}
						whileInView={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.6 }}
						viewport={{ once: true }}
						style={{
							fontSize: isMobile ? "1.8rem" : "2.4rem",
							fontWeight: 800,
							color: "#fff",
							marginBottom: "1rem",
							lineHeight: 1.2,
						}}
					>
						{t("restaurantDemo.cta.title")}
					</motion.h2>

					<motion.p
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.6, delay: 0.1 }}
						viewport={{ once: true }}
						style={{
							fontSize: "1.1rem",
							color: "rgba(255,255,255,0.9)",
							lineHeight: 1.7,
							marginBottom: "2rem",
						}}
					>
						{t("restaurantDemo.cta.subtitle")}
					</motion.p>

					<motion.div
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.6, delay: 0.2 }}
						viewport={{ once: true }}
						style={{
							display: "flex",
							gap: "12px",
							justifyContent: "center",
							flexWrap: "wrap",
						}}
					>
						<a
							href="https://creator.applaby.com?onboarding=true&appType=Ecommerce&appSubtype=Restaurant"
							target="_blank"
							rel="noreferrer"
							style={{
								display: "inline-flex",
								alignItems: "center",
								gap: "8px",
								background: "#fff",
								color: primaryColor,
								padding: "16px 32px",
								borderRadius: "12px",
								fontWeight: 700,
								fontSize: "1.05rem",
								textDecoration: "none",
								boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
							}}
						>
							{t("restaurantDemo.cta.button")}
							<ChevronRight size={20} />
						</a>
					</motion.div>

					<motion.p
						initial={{ opacity: 0 }}
						whileInView={{ opacity: 1 }}
						transition={{ duration: 0.6, delay: 0.3 }}
						viewport={{ once: true }}
						style={{
							fontSize: "0.9rem",
							color: "rgba(255,255,255,0.8)",
							marginTop: "1rem",
						}}
					>
						{t("restaurantDemo.cta.note")}
					</motion.p>
				</div>
			</section>
		</div>
	);
};

export default RestaurantDemo;
